import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { QrCode, ArrowLeft, ShieldCheck, CheckCircle } from "@phosphor-icons/react";
import { supabase } from "../lib/supabase";
import { useLang } from "../context/LangContext";
import { IMAGES } from "../lib/images";

export default function Register() {
  const { t, lang, toggle } = useLang();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState("");
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: `${window.location.origin}/login` },
    });
    setBusy(false);
    if (error) return setMsg(error.message);
    if (!data.session) return setOk(true);
    navigate("/dashboard", { replace: true });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white" data-testid="register-page">
      <div className="relative hidden lg:block order-2 lg:order-1">
        <img src={IMAGES.heroRobot} alt="Robotics" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0A0F1A]/60" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <div className="flex items-center gap-2 text-emerald-400 mb-4">
            <ShieldCheck size={22} weight="fill" />
            <span className="font-mono-tech text-xs uppercase tracking-[0.2em]">Certifico QR</span>
          </div>
          <p className="font-display font-bold text-3xl text-white max-w-md leading-tight">{t.cta.title}</p>
        </div>
      </div>

      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12 order-1 lg:order-2">
        <div className="w-full max-w-md mx-auto">
          <div className="flex items-center justify-between mb-12">
            <Link to="/" className="flex items-center gap-2.5 group" data-testid="register-logo">
              <div className="flex h-8 w-8 items-center justify-center bg-[#0A0F1A] rounded-[4px] group-hover:bg-emerald-500 transition-colors">
                <QrCode size={18} weight="bold" className="text-white" />
              </div>
              <span className="font-display font-bold text-[17px] tracking-tight">Certifico<span className="text-emerald-500">QR</span></span>
            </Link>
            <button onClick={toggle} className="font-mono-tech text-xs font-semibold border border-slate-300 rounded-[4px] px-2 py-1" data-testid="register-lang-toggle">{lang.toUpperCase()}</button>
          </div>

          {ok ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-10" data-testid="register-confirm">
              <CheckCircle size={56} weight="fill" className="mx-auto text-emerald-500" />
              <h2 className="mt-6 font-display font-bold text-2xl text-[#0F172A]">{t.auth.confirm}</h2>
              <Link to="/login" className="mt-8 inline-flex rounded-full bg-emerald-500 px-6 py-3 font-semibold text-[#04170F] hover:bg-emerald-400 transition-colors">{t.auth.login}</Link>
            </motion.div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
              <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-[#0F172A]">{t.auth.regTitle}</h1>
              <p className="mt-3 text-slate-600">{t.auth.regSub}</p>

              <form onSubmit={submit} className="mt-10 space-y-5">
                <div>
                  <label className="block font-mono-tech text-xs uppercase tracking-wider text-slate-500 mb-2">{t.auth.email}</label>
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@azienda.it"
                    className="w-full rounded-md border border-slate-300 px-4 py-3 text-[#0F172A] outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition" data-testid="register-email-input" />
                </div>
                <div>
                  <label className="block font-mono-tech text-xs uppercase tracking-wider text-slate-500 mb-2">{t.auth.password}</label>
                  <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min. 8 caratteri"
                    className="w-full rounded-md border border-slate-300 px-4 py-3 text-[#0F172A] outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition" data-testid="register-password-input" />
                </div>
                {msg && <p className="text-sm text-red-600" data-testid="register-error">{msg}</p>}
                <button type="submit" disabled={busy} className="w-full rounded-full bg-emerald-500 py-3.5 font-semibold text-[#04170F] hover:bg-emerald-400 transition-colors disabled:opacity-60" data-testid="register-submit">
                  {busy ? "…" : t.auth.register}
                </button>
              </form>

              <p className="mt-8 text-sm text-slate-600">
                {t.auth.haveAccount} <Link to="/login" className="font-semibold text-emerald-600 hover:text-emerald-700" data-testid="register-to-login">{t.auth.signin}</Link>
              </p>
              <Link to="/" className="mt-6 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0F172A] transition-colors" data-testid="register-back">
                <ArrowLeft size={16} /> {t.auth.back}
              </Link>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
